import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { userSelector } from '@/pages/user/selectors'
import { getProfile } from '@/pages/user/userSlice'
import { User } from '@/types'

interface ConnectSession {
  /** True when there is an active first-party DUPR session (the HttpOnly cookie), else false. */
  hasSession: boolean
  /** The signed-in DUPR user, or null while unknown / signed out. */
  user: User | null
  /** True while we are resolving the user profile for a detected session. */
  isInitializing: boolean
}

/**
 * Resolves the active DUPR web session for the OAuth flows.
 *
 * The first-party session lives in the HttpOnly `dupr_at` cookie now — the SPA never holds a
 * bearer token. Presence is inferred from the redux `auth.token` marker (set on login/verify),
 * and the profile is resolved via the cookie-authenticated `/user` endpoint. Every OAuth call
 * downstream (mint OTC / bind flow) sends the cookie via `withCredentials`; nothing is ever
 * exposed to a partner. This hook does not create or destroy sessions.
 */
export function useConnectSession(): ConnectSession {
  const dispatch = useAppDispatch()
  const { token: reduxToken } = useAppSelector(authSelector)
  const { user } = useAppSelector(userSelector)

  const hasSession = !!reduxToken

  const [isInitializing, setIsInitializing] = useState<boolean>(hasSession && !user)

  useEffect(() => {
    let cancelled = false

    const hydrate = async () => {
      // No session, or the profile is already loaded — nothing to resolve.
      if (!hasSession || user) {
        setIsInitializing(false)
        return
      }
      try {
        await dispatch(getProfile()).unwrap()
      } catch {
        // Leave the user null; the calling page falls back to manual login.
      } finally {
        if (!cancelled) setIsInitializing(false)
      }
    }

    hydrate()
    return () => {
      cancelled = true
    }
  }, [hasSession, user, dispatch])

  return { hasSession, user, isInitializing }
}
